import { Workflow } from 'lucide-react';
import { msg } from '@/lib/i18n';
import { registerInboxSource } from '@/lib/inbox';
import { registerInvalidation } from '@/lib/realtime';
import { registerEntityHref } from '@/ui';
import { defineModule } from '../types';
import { automationKeys } from './api';
import { failedRunsInbox, tasksInbox } from './inbox';
import { automationIndexRoute, flowRoute, flowsRoute, messageRoute, messagesRoute, newFlowRoute, runRoute, runsRoute, tasksRoute } from './routes';

/**
 * Automation: the dispatching system — flows, runs, tasks and approvals, message templates.
 * Asset rules (the old /rules) live with assets in places (/fleet/asset-rules); a rule starts an
 * automation through the trigger "an asset rule fires" (/automation/flows/new?rule=<id>).
 */
export const automationModule = defineModule({
  key: 'automation',
  routes: [automationIndexRoute, flowsRoute, newFlowRoute, flowRoute, runsRoute, runRoute, tasksRoute, messagesRoute, messageRoute],
  nav: [
    {
      key: 'automation',
      label: msg('Automation'),
      description: msg('Flows that react to events: alerts, tasks, messages and dispatch — with every run visible.'),
      icon: Workflow,
      to: '/automation',
      section: 'operate',
      order: 30,
      module: 'automation',
      permission: ['flows.flow.read', 'flows.task.work', 'flows.run.read', 'comms.template.read'],
      match: ['/automation'],
      children: [
        { key: 'flows', label: msg('Automations'), to: '/automation/flows', permission: 'flows.flow.read' },
        { key: 'runs', label: msg('Run history'), to: '/automation/runs', permission: 'flows.run.read' },
        { key: 'tasks', label: msg('Tasks and approvals'), to: '/automation/tasks', permission: 'flows.task.work' },
        { key: 'messages', label: msg('Message templates'), to: '/automation/messages', permission: 'comms.template.read' },
      ],
    },
  ],
  setup() {
    registerEntityHref('flow', (id) => `/automation/flows/${id}`);
    registerEntityHref('flow_run', (id) => `/automation/runs/${id}`);
    registerEntityHref('task', (id) => `/automation/tasks?selected=${id}`);
    // The attention inbox (Home, the top bar's bell): my tasks and approvals; failed automations.
    registerInboxSource(tasksInbox);
    registerInboxSource(failedRunsInbox);
    // Engine events are about vehicles, drivers, tasks and flows: refresh whatever automation shows.
    registerInvalidation('*', (n) => {
      if (n.type.startsWith('flows.')) return [automationKeys.all];
      if (n.type === 'comms.template.updated') return [automationKeys.messages];
      return [];
    });
  },
});
